import {Component, OnInit} from '@angular/core';
import {ReferralService} from '../../../shared/services/referral.service';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {RefPromptService} from '../shared/ref-prompt.component';
import {PER_PAGE} from '../shared/ref-utils';

type CampaignStatus = 'ACTIVE' | 'INACTIVE';

interface CampaignRow {
    id: number;
    name: string;
    code: string;
    description: string;
    status: CampaignStatus;
    startAt: string | null;
    endAt: string | null;
    createdAt: string;
}

interface CampaignDraft {
    name: string;
    code: string;
    description: string;
    // <input type="datetime-local"> value, e.g. "2026-10-07T09:30" — converted to/from ISO at the edges.
    startAt: string;
    endAt: string;
    status: CampaignStatus;
}

function emptyDraft(): CampaignDraft {
    return {name: '', code: '', description: '', startAt: '', endAt: '', status: 'ACTIVE'};
}

// Local datetime-input <-> ISO conversions. Both sides round-trip through the browser's local
// timezone, same as the native <input type="datetime-local"> contract.
function toLocalInput(iso: string | null): string {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
    return v ? new Date(v).toISOString() : null;
}

/**
 * Activate/deactivate here is just `status` on the same create/update endpoint the backend
 * already exposes (admin/referral/campaigns) — there's no separate toggle route.
 */
@Component({
    selector: 'app-referral-campaigns',
    templateUrl: './referral-campaigns.component.html'
})
export class ReferralCampaignsComponent implements OnInit {
    status = '';
    page = 0;
    per = PER_PAGE;
    total = 0;
    loading = false;
    list: CampaignRow[] = [];

    drawerOpen = false;
    saving = false;
    editingId: number | null = null;
    draft: CampaignDraft = emptyDraft();
    error = '';

    constructor(public referralService: ReferralService,
                private referralApi: ReferralApiService,
                private prompt: RefPromptService) {
    }

    ngOnInit() {
        this.load();
    }

    get pageRows(): CampaignRow[] {
        return this.list;
    }

    onFilter() {
        this.page = 0;
        this.load();
    }

    onPageChange(page: number) {
        this.page = page;
        this.load();
    }

    load() {
        this.loading = true;
        const params: any = {page: this.page + 1, per_page: this.per};
        if (this.status) {
            params.status = this.status;
        }
        this.referralApi.getCampaigns(params).subscribe({
            next: (data: any) => {
                this.total = data?.total ?? 0;
                this.list = (data?.data ?? []).map((c: any) => this.toRow(c));
                this.loading = false;
            },
            error: () => {
                this.list = [];
                this.total = 0;
                this.loading = false;
            }
        });
    }

    private toRow(c: any): CampaignRow {
        return {
            id: c.id,
            name: c.name,
            code: c.code,
            description: c.description,
            status: c.status,
            startAt: c.start_at,
            endAt: c.end_at,
            createdAt: c.created_at
        };
    }

    fmt(iso: string | null): string {
        return iso ? new Date(iso).toLocaleString('en-IN', {day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'}) : '—';
    }

    // Running right now = ACTIVE and inside the (optional) start/end window — same check the
    // referral panel's "Top Mitras" uses to decide whether to show the leaderboard at all.
    isRunning(row: CampaignRow): boolean {
        const now = Date.now();
        if (row.status !== 'ACTIVE') {
            return false;
        }
        if (row.startAt && new Date(row.startAt).getTime() > now) {
            return false;
        }
        return !(row.endAt && new Date(row.endAt).getTime() <= now);
    }

    openNew() {
        this.editingId = null;
        this.draft = emptyDraft();
        this.error = '';
        this.drawerOpen = true;
    }

    openEdit(row: CampaignRow) {
        this.editingId = row.id;
        this.draft = {
            name: row.name,
            code: row.code,
            description: row.description || '',
            startAt: toLocalInput(row.startAt),
            endAt: toLocalInput(row.endAt),
            status: row.status
        };
        this.error = '';
        this.drawerOpen = true;
    }

    close() {
        this.drawerOpen = false;
    }

    save() {
        const d = this.draft;
        if (!d.name.trim()) {
            this.error = 'Enter a campaign name.';
            return;
        }
        if (!/^[A-Z0-9_-]+$/.test(d.code.trim())) {
            this.error = 'Code must be uppercase letters, numbers, "-" or "_" only (e.g. DIWALI2026).';
            return;
        }
        if (d.startAt && d.endAt && fromLocalInput(d.endAt) <= fromLocalInput(d.startAt)) {
            this.error = 'End date must be after the start date.';
            return;
        }
        this.error = '';

        const payload = {
            name: d.name.trim(),
            code: d.code.trim(),
            description: d.description.trim() || null,
            start_at: fromLocalInput(d.startAt),
            end_at: fromLocalInput(d.endAt),
            status: d.status
        };

        this.saving = true;
        const request = this.editingId
            ? this.referralApi.updateCampaign(this.editingId, payload)
            : this.referralApi.createCampaign(payload);
        request.subscribe({
            next: () => {
                this.saving = false;
                this.drawerOpen = false;
                this.load();
            },
            error: () => {
                this.saving = false;
            }
        });
    }

    async toggleStatus(row: CampaignRow, event: Event) {
        event.stopPropagation();
        if (row.status === 'ACTIVE') {
            const reason = await this.prompt.ask({
                title: `Deactivate "${row.name}"?`,
                text: 'Partners will stop seeing this campaign\'s Top Mitras leaderboard immediately.',
                label: 'Note (optional, for your own records)',
                okText: 'Deactivate',
                check: () => ''
            });
            if (reason === null) {
                return;
            }
        }
        const nextStatus: CampaignStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        this.referralApi.updateCampaign(row.id, {status: nextStatus}).subscribe(() => {
            row.status = nextStatus;
        });
    }
}
